import { Action, createHook, createStore } from "react-sweet-state";

import {
  authenticatedDeleteFetch,
  authenticatedGetFetch,
  authenticatedPatchFetch,
  authenticatedPostFetch,
} from "utility/authenticated_fetch";
import { captureSentryException } from "utility/capture_sentry_exception";
import { ResponseError } from "utility/response_error";

import { ActionState, SecretState, SourceData } from "./lib";

// -----------------------------------------------------------------------------

const initialActionState = {
  state: ActionState.INITIAL,
};

const initialState: ConnectionStoreState = {
  connections: new Map(),
  fetchPublicKey: initialActionState,
  loadConnections: initialActionState,
  createConnection: initialActionState,
  deleteConnection: initialActionState,
  atlassianConnect: initialActionState,
  googleSheetConnect: initialActionState,
  webhookConnect: initialActionState,
  zapierWebhookConnect: initialActionState,
  splunkWebhookConnect: initialActionState,
  splunkCloudWebhookConnect: initialActionState,
  teamsWebhookConnect: initialActionState,
  tinesWebhookConnect: initialActionState,
  mattermostWebhookConnect: initialActionState,
  retryCreateConnection: initialActionState,
  webexWebhookConnect: initialActionState,
  editConnection: initialActionState,
};

// -----------------------------------------------------------------------------

const store = createStore({
  initialState,
  actions: {
    fetchPublicKey,
    loadConnections,
    createConnection,
    retryCreateConnection,
    editConnection,
    deleteConnection,

    atlassianConnect,
    googleSheetConnect,
    webhookConnect: genericWebhookConnection(
      "webhookConnect",
      "api/organization/webhook"
    ),
    zapierWebhookConnect: genericWebhookConnection(
      "zapierWebhookConnect",
      "api/organization/zapier/webhook"
    ),
    splunkWebhookConnect: genericWebhookConnection(
      "splunkWebhookConnect",
      "api/organization/splunk/webhook"
    ),
    splunkCloudWebhookConnect: genericWebhookConnection(
      "splunkCloudWebhookConnect",
      "api/organization/splunk_cloud/webhook"
    ),
    teamsWebhookConnect: genericWebhookConnection(
      "teamsWebhookConnect",
      "api/organization/teams/webhook"
    ),
    tinesWebhookConnect: genericWebhookConnection(
      "tinesWebhookConnect",
      "api/organization/tines/webhook"
    ),
    mattermostWebhookConnect: genericWebhookConnection(
      "mattermostWebhookConnect",
      "api/organization/mattermost/webhook"
    ),
    webexWebhookConnect: genericWebhookConnection(
      "webexWebhookConnect",
      "api/organization/webex/webhook"
    ),
  },
});

export const useConnectionStore = createHook(store);

// TODO: something about this; it's not an actual useEffect
export const useConnectionByID = (uuid: string) => {
  const [store, actions] = useConnectionStore();
  if (store.loadConnections.state < ActionState.COMPLETED) {
    actions.loadConnections({ reload: false });
    return undefined;
  }
  return store.connections.get(uuid) ?? undefined;
};

// -----------------------------------------------------------------------------

function fetchPublicKey({
  force,
}: FetchPublicKeyParams): Action<ConnectionStoreState> {
  return async ({ setState, getState }) => {
    if (
      getState().fetchPublicKey.state > ActionState.INITIAL ||
      (!(getState().publicKey == null) && force)
    ) {
      return;
    }

    // fetch our public key
    try {
      setState({ fetchPublicKey: { state: ActionState.IN_PROGRESS } });

      //
      const responsePayload = await authenticatedGetFetch<{
        pem: string;
        prefix: string;
        algo: string;
      }>("api/secrets/public_key");

      if (responsePayload.algo !== "RSA-OAEP") {
        throw new Error(
          `got unsupported encryption scheme: ${responsePayload.algo}`
        );
      }

      // import it into a RSA-OAEP CryptoKey object
      const trimmedPEM = responsePayload.pem.trim();
      const pKeyB64 = trimmedPEM
        .substring(
          "-----BEGIN PUBLIC KEY-----".length,
          trimmedPEM.length - "-----END PUBLIC KEY-----".length
        )
        .trim();
      const der = window.atob(pKeyB64);
      const buf = string2Buffer(der);

      //
      const pubKey = await crypto.subtle.importKey(
        "spki",
        buf,
        {
          name: "RSA-OAEP",
          hash: "SHA-256",
        },
        true,
        ["encrypt"]
      );

      setState({
        fetchPublicKey: { state: ActionState.COMPLETED },
        publicKey: pubKey,
        payloadPrefix: responsePayload.prefix,
      });
    } catch (error) {
      captureSentryException(error, "Failed to import public key");
      setState({
        fetchPublicKey: {
          state: ActionState.ERROR,
          error: "error importing public key",
        },
      });
    }
  };
}

function loadConnections({ reload }: LoadParams): Action<ConnectionStoreState> {
  return async ({ setState, getState }) => {
    if (getState().loadConnections.state > ActionState.INITIAL && !reload) {
      return;
    }

    //
    try {
      setState({ loadConnections: { state: ActionState.IN_PROGRESS } });

      //
      const connections = await loadAllConnections();
      setState({
        loadConnections: { state: ActionState.COMPLETED },
        connections,
      });
    } catch (error) {
      captureSentryException(error, "Failed to load connections");
      setState({
        loadConnections: {
          state: ActionState.ERROR,
          error: (error as Error).message,
        },
      });
    }
  };
}

function createConnection({
  kind,
  data,
  onSuccess,
  onError,
}: CreateParams): Action<ConnectionStoreState> {
  return async ({ setState, getState }) => {
    if (getState().createConnection.state === ActionState.IN_PROGRESS) {
      return;
    }

    //
    const publicKey = getState().publicKey;
    if (publicKey === undefined) {
      setState({
        createConnection: {
          state: ActionState.ERROR,
          error: "public key not loaded",
        },
      });
      onError?.();
      return;
    }

    //
    const payloadPrefix = getState().payloadPrefix;
    if (payloadPrefix === undefined) {
      setState({
        createConnection: {
          state: ActionState.ERROR,
          error: "payload prefix not loaded",
        },
      });
      onError?.();
      return;
    }

    //
    try {
      setState({ createConnection: { state: ActionState.IN_PROGRESS } });

      //
      const inputBuffer = string2Buffer(JSON.stringify(data));
      const ciphertextBytes = await crypto.subtle.encrypt(
        { name: "RSA-OAEP" },
        publicKey,
        inputBuffer
      );
      const cipherTextB64 = window.btoa(
        String.fromCharCode(...new Uint8Array(ciphertextBytes))
      );
      const responsePayload = await authenticatedPostFetch<{ uuid: string }>(
        "api/secrets",
        {
          kind,
          secret: `${payloadPrefix}${cipherTextB64}`,
        }
      );
      setState({
        createConnection: {
          state: ActionState.COMPLETED,
        },
      });
      onSuccess?.({ kind, uuid: responsePayload.uuid });
    } catch (error) {
      captureSentryException(error, "Failed to create connection");
      setState({
        createConnection: {
          state: ActionState.ERROR,
          error: (error as Error).message,
        },
      });
      onError?.();
    }
  };
}

function retryCreateConnection({
  uuid,
  onSuccess,
  onError,
}: RetryCreateConnectionParams): Action<ConnectionStoreState> {
  return async ({ getState, dispatch }) => {
    let connections = getState().connections;
    if (!connections.has(uuid)) {
      await dispatch(loadConnections({ reload: true }));
      connections = getState().connections;

      if (!connections.has(uuid)) {
        onError?.();
        return;
      }
    }

    //
    dispatch(deleteConnection({ uuid, onSuccess, onError }));
  };
}

function editConnection({
  uuid,
  data,
  onSuccess,
  onError,
}: EditConnectionParams): Action<ConnectionStoreState> {
  return async ({ getState, setState }) => {
    if (getState().editConnection.state === ActionState.IN_PROGRESS) {
      return;
    }

    //
    try {
      setState({ editConnection: { state: ActionState.IN_PROGRESS } });

      //
      const connection = await authenticatedPatchFetch(
        `api/organization/connections/${uuid}`,
        data
      );
      setState({
        editConnection: {
          state: ActionState.COMPLETED,
        },
      });

      const newConnection = {
        uuid: connection.uuid,
        clientSecret: connection.client_secret
          ? convertSecretData(connection.client_secret)
          : null,
        config: connection.config,
        kind: connection.kind,
        name: connection.name,
      };

      const connections = structuredClone(getState().connections);
      connections.set(connection.uuid, newConnection);

      setState({
        connections,
      });
      onSuccess?.();
    } catch (error) {
      captureSentryException(error, "Failed to edit connection");
      setState({
        createConnection: {
          state: ActionState.ERROR,
          error: (error as Error).message,
        },
      });
      onError?.();
    }
  };
}

function deleteConnection({
  uuid,
  onSuccess,
  onError,
}: DeleteParams): Action<ConnectionStoreState> {
  return async ({ getState, setState }) => {
    if (getState().deleteConnection.state === ActionState.IN_PROGRESS) {
      return;
    }

    //
    try {
      setState({ deleteConnection: { state: ActionState.IN_PROGRESS } });

      //
      await authenticatedDeleteFetch(`api/organization/connections/${uuid}`);

      //
      const connections = getState().connections;
      if (connections.has(uuid)) {
        connections.delete(uuid);
      }

      setState({
        connections,
      });

      setState({ deleteConnection: { state: ActionState.COMPLETED } });
      onSuccess?.();
    } catch (error) {
      captureSentryException(error, "Failed to delete connection");
      setState({
        deleteConnection: {
          state: ActionState.ERROR,
          error: "There was an error deleting the connection",
        },
      });
      onError?.();
    }
  };
}

function atlassianConnect({
  nonce,
  onSuccess,
  onError,
}: AtlassianConnectParams): Action<ConnectionStoreState> {
  return async ({ getState, setState }) => {
    if (getState().atlassianConnect.state === ActionState.IN_PROGRESS) {
      return;
    }

    //
    try {
      setState({ atlassianConnect: { state: ActionState.IN_PROGRESS } });

      const responsePayload = await authenticatedPostFetch(
        `api/organization/atlassian_connect`,
        {
          nonce,
        }
      );

      setState({ atlassianConnect: { state: ActionState.COMPLETED } });
      onSuccess?.(responsePayload);
    } catch (error) {
      captureSentryException(error, "Failed to connect to Atlassian");
      setState({
        atlassianConnect: {
          state: ActionState.ERROR,
          error: (error as Error).message,
        },
      });
      onError?.();
    }
  };
}

function googleSheetConnect({
  title,
  email,
  onSuccess,
  onError,
}: GoogleSheetConnectParams): Action<ConnectionStoreState> {
  return async ({ getState, setState }) => {
    if (getState().googleSheetConnect.state === ActionState.IN_PROGRESS) {
      return;
    }

    //
    try {
      setState({ googleSheetConnect: { state: ActionState.IN_PROGRESS } });

      const responsePayload = await authenticatedPostFetch(
        `api/organization/google_sheet`,
        { title, email }
      );

      setState({ googleSheetConnect: { state: ActionState.COMPLETED } });
      onSuccess?.(responsePayload);
    } catch (error) {
      captureSentryException(error, "Failed to connect to Google Sheet");
      setState({
        googleSheetConnect: {
          state: ActionState.ERROR,
          error: (error as Error).message,
        },
      });
      onError?.((error as ResponseError)?.payload);
    }
  };
}

// -----------------------------------------------------------------------------

export async function loadAllConnections(): Promise<
  ConnectionStoreState["connections"]
> {
  let page = 1;
  const connections = new Map<string, Connection>();

  //
  while (true) {
    const responsePayload = await authenticatedGetFetch<{
      results: ConnectionData[];
      next: string | null;
    }>(`api/organization/connections?page=${page}`);

    (responsePayload.results ?? []).forEach((connection) => {
      connections.set(connection.uuid, {
        uuid: connection.uuid,
        clientSecret: connection.client_secret
          ? convertSecretData(connection.client_secret)
          : null,
        config: connection.config,
        kind: connection.kind,
        name: connection.name,
      });
    });

    //
    page += 1;
    if (responsePayload.next === null) {
      break;
    }
  }

  //
  return connections;
}

function genericWebhookConnection(key: string, path: string) {
  return ({
      webhookUrl,
      onSuccess,
      onError,
    }: WebhookConnectParams): Action<ConnectionStoreState> =>
    async ({ getState, setState }) => {
      const state = getState() as any;
      if (state?.[key]?.state === ActionState.IN_PROGRESS) {
        return;
      }

      try {
        setState({ [key]: { state: ActionState.IN_PROGRESS } });
        const responsePayload = await authenticatedPostFetch(path, {
          webhook_url: webhookUrl,
        });
        setState({ [key]: { state: ActionState.COMPLETED } });
        onSuccess?.(responsePayload);
      } catch (error) {
        captureSentryException(
          error,
          `Failed to create ${key} webhook connection store`
        );
        setState({
          webhookConnect: {
            state: ActionState.ERROR,
            error: (error as Error).message,
          },
        });
        onError?.();
      }
    };
}

function convertSecretData(secret: SecretData): SecretInfo {
  return {
    uuid: secret.uuid,
    name: secret.name,
    state: secret.state,
    stateDetails: secret.state_details,
    created: new Date(secret.created_at),
    updated: new Date(secret.last_updated),
  };
}

function string2Buffer(input: string): ArrayBuffer {
  const buffer = new ArrayBuffer(input.length);
  const bufferView = new Uint8Array(buffer);
  for (let i = 0; i < input.length; i++) {
    bufferView[i] = input.charCodeAt(i);
  }
  return buffer;
}

// -----------------------------------------------------------------------------

interface ConnectionStoreState {
  publicKey?: CryptoKey;
  payloadPrefix?: string;
  connections: Map<string, Connection>;
  // Action States
  fetchPublicKey: ActionInfo;
  loadConnections: ActionInfo;
  createConnection: ActionInfo;
  deleteConnection: ActionInfo;
  atlassianConnect: ActionInfo;
  googleSheetConnect: ActionInfo;
  webhookConnect: ActionInfo;
  zapierWebhookConnect: ActionInfo;
  splunkWebhookConnect: ActionInfo;
  splunkCloudWebhookConnect: ActionInfo;
  teamsWebhookConnect: ActionInfo;
  tinesWebhookConnect: ActionInfo;
  mattermostWebhookConnect: ActionInfo;
  webexWebhookConnect: ActionInfo;
  retryCreateConnection: ActionInfo;
  editConnection: ActionInfo;
}

interface ActionInfo {
  state: ActionState;
  error?: string;
}

// SecretData is the on-the-wire format of a secret, which only exposed for some useSWR usage.
export interface SecretData {
  uuid: string;
  name: string;
  state: SecretState;
  state_details: string;
  created_at: string;
  last_updated: string;
}

export interface SecretInfo {
  uuid: string;
  name: string;
  state: SecretState;
  stateDetails: string;
  created: Date;
  updated: Date;
}

// TS-friendly objects
export interface Connection {
  uuid: string;
  clientSecret: SecretInfo | null;
  config: { [name: string]: any; tld?: string };
  kind: string;
  name: string;
}

// API objects
export interface ConnectionData {
  uuid: string;
  client_secret: SecretData;
  config: { [name: string]: string };
  kind: string;
  name: string;
}

interface FetchPublicKeyParams {
  force: boolean;
}

interface LoadParams {
  reload: boolean;
}

interface CreateParams {
  kind: string;
  data: SourceData;
  onSuccess?: Function;
  onError?: Function;
}

interface RetryCreateConnectionParams {
  uuid: string;
  onSuccess?: Function;
  onError?: Function;
}

interface EditConnectionParams {
  uuid: string;
  data: any;
  onSuccess?: Function;
  onError?: Function;
}

interface DeleteParams {
  uuid: string;
  onSuccess?: Function;
  onError?: Function;
}

interface AtlassianConnectParams {
  nonce: string;
  onSuccess?: Function;
  onError?: Function;
}

interface WebhookConnectParams {
  webhookUrl: string;
  onSuccess?: Function;
  onError?: Function;
}

interface GoogleSheetConnectParams {
  title: string;
  email: string;
  onSuccess?: (response: { connection_id: string; url: string }) => void;
  onError?: (response: { [field: string]: string }) => void;
}
